// The Matrix at the table (issue 1532; docs/MRCS/shadowrun-3.md §9 step 18): only where the table's system names a
// System Test (so a d20 table never sees either part).
//
// - `SystemTestForm`, behind "System test…" in the dice bag: the decker (a token of theirs, or any for the DM), the
//   subsystem the operation works on, the skill (the sheet's row the system names, or a rating typed), Hacking Pool
//   dice when the decker is in the turn order, and the utilities that help the operation as typed TN modifiers. The
//   browser names that and nothing the server decides: the host, its ratings, the TN and the tally are the server's,
//   read from the decker's token where only the DM puts them, and never in this player's store.
// - `HostEditor`, in the DM's token panel: the host a decker's token is in — its name, security code, Security Value,
//   each subsystem's rating, its alert, and whether its ratings are shown to the decker's players — and the tally the
//   server keeps there.
import { useEffect, useState } from 'react';
import { CYBER_LIMITS, HOST_LIMITS, MATRIX_TEST_LIMITS, addsDice, drawsFor, poolCap, type MatrixSeen, type MatrixState, type SeenPool } from '@dragons-table/vtt-shared';
import { searchCompendium, type CompendiumHit } from '../../compendium';
import { PromptDialog } from '../Dialogs';
import { selection } from '../../board/selection';
import { fetchTests, type TestsAnswer } from '../../entries';
import { asksFor } from './asksFor';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null }
interface Fighting { tokens: string[]; pools?: Record<string, SeenPool> }

const TYPED = 'typed';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);

export function SystemTestForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.matrix;
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => (isDm ? true : o.mine));
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? '');
  const [subsystem, setSubsystem] = useState(view?.subsystems[0]?.key ?? '');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [skill, setSkill] = useState(TYPED);
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('4');
  const [poolDice, setPoolDice] = useState('0');
  const [utilLabel, setUtilLabel] = useState('');
  const [utilValue, setUtilValue] = useState('0');
  const [extraLabel, setExtraLabel] = useState('');
  const [extraValue, setExtraValue] = useState('0');
  const [typedDetection, setTypedDetection] = useState('');

  const decker = tokens.find((o) => o.id === who);
  useEffect(() => {
    setAnswer(null); setSkill(TYPED);
    if (!decker?.character) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, who, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      // The system's skill — its specialization first, as a decker rolls Computer (Decking) — else the first skill.
      const skills = (a?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
      const spec = skills.findIndex((c) => view?.skill && c.ref === view.skill && c.kind === 'specialization');
      const named = skills.findIndex((c) => view?.skill && c.ref === view.skill);
      if (spec >= 0) setSkill(String(spec));
      else if (named >= 0) setSkill(String(named));
      else if (skills.length) setSkill('0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, decker?.character, campaignId]);

  const got = answer && answer !== 'loading' ? answer : null;
  const skills = (got?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
  const chosenSkill = skill === TYPED ? null : skills[Number(skill)] ?? null;
  const rating = chosenSkill ? chosenSkill.rating : int(typedRating);
  const skillName = chosenSkill ? chosenSkill.name : typedSkill.trim();
  // Pool dice from the system's System Test pool, when the decker is in the turn order: at most the skill.
  const enc = store.state.encounter as { combatants?: Fighting[] } | undefined;
  const fighting = enc?.combatants?.find((c) => c.tokens.includes(who));
  const pool = (systemRules.pools ?? []).find((p) => p.key === view?.pool && addsDice(p) && drawsFor(p, 'matrix') && fighting?.pools?.[p.key]) ?? null;
  const left = pool ? fighting!.pools![pool.key]!.left : 0;
  const cap = pool && rating ? poolCap(pool, 'matrix', rating) : null;
  const most = Math.min(left, cap ?? left);
  const poolN = int(poolDice) ?? 0;
  const ready = Boolean(view && decker && subsystem && rating && rating >= 1 && skillName && poolN >= 0 && poolN <= most);

  if (!view) return null;
  if (!mine.length) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="matrix-form">
        You have no token at the table to deck with.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  const term = (l: string, v: number) => (l.trim() && v ? [{ label: l.trim().slice(0, MATRIX_TEST_LIMITS.label), value: Math.max(-MATRIX_TEST_LIMITS.modifier, Math.min(MATRIX_TEST_LIMITS.modifier, v)) }] : []);
  const sub = view.subsystems.find((x) => x.key === subsystem);
  return (
    <form className="space-y-1 rounded border border-emerald-800 p-2 text-xs" data-testid="matrix-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !rating) return;
      const body = {
        kind: 'matrix.test', decker: who, subsystem,
        skill: { name: skillName.slice(0, MATRIX_TEST_LIMITS.name), rating, ...(chosenSkill?.ref ? { ref: chosenSkill.ref } : {}) },
        ...(pool && poolN > 0 ? { pool: { key: pool.key, dice: poolN } } : {}),
        modifiers: [...term(utilLabel, int(utilValue) ?? 0), ...term(extraLabel, int(extraValue) ?? 0)],
        ...(int(typedDetection) !== null && int(typedDetection)! >= 1 ? { detection: Math.min(MATRIX_TEST_LIMITS.detection, int(typedDetection)!) } : {}),
        private: isDm ? priv : false,
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That System Test could not be made.'); else onClose(); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Decker
          <select aria-label="Decker" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">tests
          <select aria-label="Subsystem" className={field} value={subsystem} onChange={(e) => setSubsystem(e.target.value)}>
            {view.subsystems.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300">{got.notice}</p>}
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Skill
          <select aria-label="Decker's skill" className={field} value={skill} onChange={(e) => setSkill(e.target.value)}>
            {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
            <option value={TYPED}>A rating typed in…</option>
          </select>
        </label>
        {skill === TYPED && (
          <>
            <input aria-label="Decker's skill name" placeholder="Computer" maxLength={MATRIX_TEST_LIMITS.name} className={`${field} w-24`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
            <input aria-label="Decker's skill rating" type="number" min={1} max={MATRIX_TEST_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
          </>
        )}
        {pool && (
          <label className="flex items-center gap-1" data-testid="matrix-pool">{pool.label}
            <input aria-label="Pool dice for the System Test" type="number" min={0} max={most} className={`${field} w-12`} value={poolDice} onChange={(e) => setPoolDice(e.target.value)} />
            <span className="text-stone-400">of {left}</span>
          </label>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1" title="A utility that helps the operation lowers the TN by its rating: type it as a negative number.">
        <span className="text-stone-400">TN</span>
        <input aria-label="Utility label" placeholder="Deception" maxLength={MATRIX_TEST_LIMITS.label} className={`${field} w-20`} value={utilLabel} onChange={(e) => setUtilLabel(e.target.value)} />
        <input aria-label="Utility modifier to the TN" type="number" className={`${field} w-12`} value={utilValue} onChange={(e) => setUtilValue(e.target.value)} />
        <input aria-label="Other modifier label" placeholder="other" maxLength={MATRIX_TEST_LIMITS.label} className={`${field} w-20`} value={extraLabel} onChange={(e) => setExtraLabel(e.target.value)} />
        <input aria-label="Other modifier to the TN" type="number" className={`${field} w-12`} value={extraValue} onChange={(e) => setExtraValue(e.target.value)} />
      </div>
      {asksFor(decker?.character, answer, 'matrix.detection') && (
        <label className="flex items-center gap-1" data-testid="matrix-typed-detection" title="Only when the decker's sheet has none.">{view.detection}
          <input aria-label="Decker's Detection Factor" type="number" min={1} max={MATRIX_TEST_LIMITS.detection} className={`${field} w-12`} value={typedDetection} onChange={(e) => setTypedDetection(e.target.value)} />
        </label>
      )}
      {sub && rating && <p data-testid="matrix-summary">{sub.label} Test · {rating}{poolN ? ` + ${poolN}` : ''} dice against the host's {sub.label} rating · the host's {view.security} against your {view.detection}</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-emerald-700`} disabled={!ready}>Test</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: the players see nothing of it.' : 'Public: the players see the decker\'s dice and whether it succeeded, never the host.'}</span>}
      </div>
    </form>
  );
}

/**
 * The host a decker's token is in (issue 1532): the DM's alone, as the server sends it to the DM alone. Saving sends the
 * whole host and the tally as they stand here; Take out of the host clears both.
 */
export function HostEditor({ o }: { o: { id: string; kind: string; matrix?: MatrixState } }) {
  const { act, setNotice, systemRules } = useTableState();
  const view = systemRules.matrix;
  const m = o.matrix;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(m?.host.name ?? '');
  const [code, setCode] = useState(m?.host.code ?? view?.codes[0]?.key ?? '');
  const [security, setSecurity] = useState(String(m?.host.security ?? 6));
  const [ratings, setRatings] = useState<Record<string, string>>(Object.fromEntries((view?.subsystems ?? []).map((s) => [s.key, String(m?.host.ratings[s.key] ?? 10)])));
  const [alert, setAlert] = useState(m?.host.alert ?? '');
  const [shown, setShown] = useState(m?.host.shown ?? false);
  const [tally, setTally] = useState(String(m?.tally ?? 0));
  useEffect(() => { setTally(String(m?.tally ?? 0)); }, [m?.tally]);
  // The security sheaf (qa-runs#1173): a row a step, an IC of the system's IC family by its slug and rating, and/or an alert.
  type Row = { step: string; ic: string; rating: string; alert: string };
  const rowsOf = (): Row[] => (m?.host.sheaf ?? []).map((x) => ({ step: String(x.step), ic: x.ic?.split('/')[1] ?? '', rating: x.rating ? String(x.rating) : '', alert: x.alert ?? '' }));
  const [rows, setRows] = useState<Row[]>(rowsOf);
  const [sheafOff, setSheafOff] = useState(Boolean(m?.host.sheafOff));
  // The IC the sheaf may name (qa-runs#1173): the system's IC family's entries, from the compendium as the DM types.
  const { campaignId } = useShell();
  const [icQuery, setIcQuery] = useState('');
  const [icHits, setIcHits] = useState<CompendiumHit[]>([]);
  const [launching, setLaunching] = useState(false);
  useEffect(() => {
    if (!view?.sheaf || !open) return;
    let live = true;
    const t = setTimeout(() => { void searchCompendium(campaignId, icQuery || view.sheaf!, null).then((a) => { if (live && a.kind === 'ok') setIcHits(a.hits.filter((h) => h.family === view.sheaf)); }); }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [icQuery, open, campaignId, view?.sheaf]);
  if (!view || o.kind !== 'token') return null;
  const codeLabel = view.codes.find((c) => c.key === m?.host.code)?.label;
  if (!open) {
    return (
      <div className="flex items-center gap-1 text-xs" data-testid="host-summary">
        <span className="text-stone-400">Matrix:</span>
        <span>{m ? `${m.host.name ? `${m.host.name}, ` : ''}${codeLabel ?? m.host.code}-${m.host.security} · ${view.tally} ${m.tally}` : 'in no host'}</span>
        <button type="button" className={btn} onClick={() => setOpen(true)}>{m ? 'Host…' : 'Put in a host…'}</button>
      </div>
    );
  }
  const secN = int(security); const tallyN = int(tally);
  const ratingN = Object.fromEntries(view.subsystems.map((s) => [s.key, int(ratings[s.key] ?? '')]));
  const ok = Boolean(code && secN && secN >= 1 && secN <= HOST_LIMITS.security && tallyN !== null && tallyN >= 0 && tallyN <= HOST_LIMITS.tally
    && Object.values(ratingN).every((r) => r !== null && r >= 1 && r <= HOST_LIMITS.rating));
  const say = (reply: { ok: boolean; reason?: string }) => { if (!reply.ok) setNotice(reply.reason ?? 'That host could not be set.'); else setOpen(false); };
  const sheafOf = (list: Row[]) => list.flatMap((r) => {
    const step = int(r.step); const rating = int(r.rating); const slug = r.ic.trim().toLowerCase().replace(/\s+/g, '-');
    if (!step || step < 1) return [];
    const ic = view.sheaf && slug && rating ? { ic: `${view.sheaf}/${slug}`, rating } : {};
    const al = r.alert ? { alert: r.alert } : {};
    return 'ic' in ic || r.alert ? [{ step, ...ic, ...al }] : [];
  }).slice(0, HOST_LIMITS.steps);
  // Sending the host keeps what the server wrote of it (the steps fired, the IC launched) for the steps still there.
  const send = (list: Row[]) => {
    const sheaf = sheafOf(list);
    const host = { ...(name.trim() ? { name: name.trim().slice(0, HOST_LIMITS.name) } : {}), code, security: secN!, ratings: ratingN as Record<string, number>, alert: alert || null, shown, ...(sheaf.length ? { sheaf } : {}), ...(sheafOff ? { sheafOff: true } : {}) };
    const fired = (m?.fired ?? []).filter((i) => i < sheaf.length);
    act({ kind: 'object.matrix', id: o.id, matrix: { host, tally: tallyN!, ...(fired.length ? { fired } : {}), ...(m?.ic?.length ? { ic: m.ic } : {}), ...(m?.persona ? { persona: m.persona } : {}), ...(m?.mpcpLost ? { mpcpLost: m.mpcpLost } : {}), ...(m?.lowered ? { lowered: m.lowered } : {}) } } as never, say);
  };
  return (
    <form className="space-y-1 rounded border border-emerald-900 p-2 text-xs" data-testid="host-editor" onSubmit={(e) => {
      e.preventDefault();
      if (!ok) return;
      send(rows);
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <input aria-label="Host name" placeholder="Host" maxLength={HOST_LIMITS.name} className={`${field} w-28`} value={name} onChange={(e) => setName(e.target.value)} />
        <select aria-label="Security code" className={field} value={code} onChange={(e) => setCode(e.target.value)}>
          {view.codes.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <label className="flex items-center gap-1">{view.security}
          <input aria-label={view.security} type="number" min={1} max={HOST_LIMITS.security} className={`${field} w-12`} value={security} onChange={(e) => setSecurity(e.target.value)} />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {view.subsystems.map((s) => (
          <label key={s.key} className="flex items-center gap-1">{s.label}
            <input aria-label={`${s.label} rating`} type="number" min={1} max={HOST_LIMITS.rating} className={`${field} w-12`} value={ratings[s.key] ?? ''} onChange={(e) => setRatings({ ...ratings, [s.key]: e.target.value })} />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {view.alerts.length > 0 && (
          <select aria-label="Alert" className={field} value={alert} onChange={(e) => setAlert(e.target.value)}>
            <option value="">No alert</option>
            {view.alerts.map((a) => <option key={a.key} value={a.key}>{a.label}{a.add ? ` (+${a.add})` : ''}</option>)}
          </select>
        )}
        <label className="flex items-center gap-1">{view.tally}
          <input aria-label={view.tally} type="number" min={0} max={HOST_LIMITS.tally} className={`${field} w-14`} value={tally} onChange={(e) => setTally(e.target.value)} />
        </label>
        <label className="flex items-center gap-1" title="The decker's players then read the host's ratings, its test and the TN in their copy of each System Test. Never the tally.">
          <input type="checkbox" aria-label="Show the host's ratings to the decker's players" checked={shown} onChange={(e) => setShown(e.target.checked)} /> Shown to the players
        </label>
      </div>
      {view.alerts.length > 0 && (
        <fieldset className="space-y-1" data-testid="sheaf-editor">
          <legend className="text-stone-400">Security sheaf (only you see it): at each step of the {view.tally.toLowerCase()}, IC launches or an alert is raised</legend>
          {rows.map((r, i) => {
            const fired = m?.fired?.includes(i);
            const set = (patch: Partial<Row>) => setRows(rows.map((x, k) => (k === i ? { ...x, ...patch } : x)));
            return (
              <div key={i} className="flex flex-wrap items-center gap-1" data-testid="sheaf-step">
                <label className="flex items-center gap-1">Step
                  <input aria-label={`Step ${i + 1}`} type="number" min={1} max={HOST_LIMITS.tally} className={`${field} w-14`} value={r.step} onChange={(e) => set({ step: e.target.value })} />
                </label>
                {view.sheaf && <input aria-label={`IC of step ${i + 1}`} placeholder="IC" list="sheaf-ic-entries" className={`${field} w-28`} value={r.ic} onChange={(e) => { set({ ic: e.target.value }); setIcQuery(e.target.value); }} />}
                {view.sheaf && <input aria-label={`IC rating of step ${i + 1}`} placeholder="Rating" type="number" min={1} max={HOST_LIMITS.rating} className={`${field} w-14`} value={r.rating} onChange={(e) => set({ rating: e.target.value })} />}
                <select aria-label={`Alert of step ${i + 1}`} className={field} value={r.alert} onChange={(e) => set({ alert: e.target.value })}>
                  <option value="">No alert</option>
                  {view.alerts.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                </select>
                {fired && <span className="text-amber-400">fired</span>}
                <button type="button" className={btn} aria-label={`Remove step ${i + 1}`} onClick={() => setRows(rows.filter((_, k) => k !== i))}>×</button>
              </div>
            );
          })}
          <div className="flex flex-wrap items-center gap-1">
            <button type="button" className={btn} disabled={rows.length >= HOST_LIMITS.steps} onClick={() => setRows([...rows, { step: String(Math.max(1, (tallyN ?? 0) + 1)), ic: '', rating: '', alert: '' }])}>Add a step</button>
            {view.sheaf && m && <button type="button" className={btn} disabled={!ok || rows.length >= HOST_LIMITS.steps} title="Launch an IC now: a step at the tally as it stands, which fires at once." onClick={() => setLaunching(true)}>Launch IC now…</button>}
            {launching && <PromptDialog title="Launch IC now" label="Which IC, and at what rating (for example: killer 6)" submitLabel="Launch" maxLength={60}
              note={icHits.length ? `IC: ${icHits.map((h) => h.slug).join(', ')}` : undefined}
              onClose={() => setLaunching(false)} onSubmit={(value) => {
                const parts = value.trim().toLowerCase().split(/\s+/);
                const rating = int(parts.pop() ?? '');
                const ic = parts.join('-');
                if (!ic || !rating || rating < 1 || rating > HOST_LIMITS.rating) return 'Name an IC and its rating, as: killer 6';
                if (icHits.length && !icHits.some((h) => h.slug === ic)) return `There is no IC "${ic}" in the compendium.`;
                const next = [...rows, { step: String(Math.max(1, tallyN ?? 0)), ic, rating: String(rating), alert: '' }];
                setRows(next); send(next); setLaunching(false);
                return null;
              }} />}
            <datalist id="sheaf-ic-entries">{icHits.map((h) => <option key={h.slug} value={h.slug}>{h.name}</option>)}</datalist>
            <label className="flex min-h-[2.25rem] items-center gap-1"><input type="checkbox" aria-label="Sheaf off" checked={sheafOff} onChange={(e) => setSheafOff(e.target.checked)} /> Sheaf off (nothing launches by itself)</label>
          </div>
        </fieldset>
      )}
      {m?.ic?.length ? <p className="text-stone-400" data-testid="host-ic">IC launched: {m.ic.map((x) => `${x.name}-${x.rating}`).join(', ')}</p> : null}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-emerald-700`} disabled={!ok}>Save</button>
        {m && <button type="button" className={btn} onClick={() => act({ kind: 'object.matrix', id: o.id, matrix: null } as never, say)}>Take out of the host</button>}
        <button type="button" className={btn} onClick={() => setOpen(false)}>Close</button>
      </div>
    </form>
  );
}

/**
 * Cybercombat (qa-runs#1173 part 3), in the decker's token panel: the IC in its host — every launched one for the DM, only
 * those the decker has met for its player (`matrixSeen`, the server's slice) — each with its icon monitor, Attack… (the
 * attack utility, its rating and level, and Hacking Pool dice) and, for the DM, Act (the IC acts by its entry). The TN,
 * the Security Value and the staging are the server's.
 */
export function CyberPanel({ o, isDm }: { o: { id: string; kind: string; jacked?: true; matrix?: MatrixState; matrixSeen?: MatrixSeen }; isDm: boolean }) {
  const { act, setNotice, systemRules } = useTableState();
  const view = systemRules.matrix;
  const levels = systemRules.staged?.codes.levels ?? [];
  const [aiming, setAiming] = useState<string | null>(null);
  const [name, setName] = useState('Attack');
  const [rating, setRating] = useState('5');
  const [level, setLevel] = useState(levels[1]?.key ?? levels[0]?.key ?? '');
  const [pool, setPool] = useState('0');
  if (!view?.cyber || o.kind !== 'token' || !o.jacked) return null;
  const list = isDm ? (o.matrix?.ic ?? []).map((x) => ({ name: x.name, rating: x.rating, combatant: x.combatant, damage: x.damage ?? 0 })) : o.matrixSeen?.ic ?? [];
  const persona = isDm ? o.matrix?.persona ?? 0 : o.matrixSeen?.persona ?? 0;
  if (!list.length && !persona) return null;
  const say = (reply: { ok: boolean; reason?: string; notice?: string }) => { setNotice(reply.ok ? reply.notice ?? 'Done.' : reply.reason ?? 'That could not be done.'); if (reply.ok) setAiming(null); };
  const r = int(rating); const p = int(pool) ?? 0;
  const ok = Boolean(name.trim() && r && r >= 1 && r <= CYBER_LIMITS.rating && level && p >= 0 && p <= CYBER_LIMITS.pool);
  return (
    <div className="space-y-1 text-xs" data-testid="cyber-panel">
      <p className="text-stone-400">Persona monitor: {persona}/10 boxes{isDm && o.matrix?.mpcpLost ? ` · MPCP burned out at the table: ${o.matrix.mpcpLost}` : ''}</p>
      {list.map((ic) => (
        <div key={ic.combatant} className="flex flex-wrap items-center gap-1" data-testid="cyber-ic">
          <span>{ic.name}-{ic.rating} (Matrix): {ic.damage} boxes</span>
          <button type="button" className={btn} onClick={() => setAiming(aiming === ic.combatant ? null : ic.combatant)}>Attack…</button>
          {isDm && <button type="button" className={btn} title="The IC acts against the decker, by its entry." onClick={() => act({ kind: 'matrix.cyber', act: 'ic', decker: o.id, ic: ic.combatant } as never, say)}>Act</button>}
          {aiming === ic.combatant && (
            <form className="flex w-full flex-wrap items-center gap-1" data-testid="cyber-attack" onSubmit={(e) => {
              e.preventDefault();
              if (!ok) return;
              act({ kind: 'matrix.cyber', act: 'attack', decker: o.id, ic: ic.combatant, utility: { name: name.trim(), rating: r!, level }, pool: p } as never, say);
            }}>
              <input aria-label="Attack utility" maxLength={CYBER_LIMITS.name} className={`${field} w-24`} value={name} onChange={(e) => setName(e.target.value)} />
              <input aria-label="Utility rating" type="number" min={1} max={CYBER_LIMITS.rating} className={`${field} w-12`} value={rating} onChange={(e) => setRating(e.target.value)} />
              <select aria-label="Damage level" className={field} value={level} onChange={(e) => setLevel(e.target.value)}>
                {levels.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
              <label className="flex items-center gap-1">Hacking Pool
                <input aria-label="Hacking Pool dice" type="number" min={0} max={CYBER_LIMITS.pool} className={`${field} w-12`} value={pool} onChange={(e) => setPool(e.target.value)} />
              </label>
              <button type="submit" className={`${btn} border-emerald-700`} disabled={!ok}>Attack</button>
            </form>
          )}
        </div>
      ))}
    </div>
  );
}
